import { memo, useCallback } from "react";
import { useWatch } from "react-hook-form";
import type { FilterRangeData } from "@/api/filter-ranges";
import { SimpleRangeSlider } from "@/components/base/simple-range-slider";
import { LabelWithTooltip } from "@/components/filters/shared";
import type { useFilters } from "@/providers/filter-provider";

type FollowersRangeFilterProps = {
  form: ReturnType<typeof useFilters>["form"];
  min?: number;
  max?: number;
  rangeData?: FilterRangeData;
};

export const FollowersRangeFilter = memo(({ form, min, max, rangeData }: FollowersRangeFilterProps) => {
  // Support both direct min/max props and rangeData for backwards compatibility
  const rangeMin = min ?? rangeData?.min ?? 0;
  const rangeMax = max ?? rangeData?.max ?? 10000000;

  // Use useWatch for efficient subscriptions to specific fields
  const minFollowers = useWatch({ control: form.control, name: "minFollowers" });
  const maxFollowers = useWatch({ control: form.control, name: "maxFollowers" });

  const minValue = Number(minFollowers) || rangeMin;
  const maxValue = Number(maxFollowers) || rangeMax;

  const handleMinChange = useCallback(
    (value: number) =>
      form.setValue("minFollowers", value === rangeMin ? undefined : String(value), {
        shouldDirty: true,
        shouldTouch: true,
      }),
    [form, rangeMin],
  );

  const handleMaxChange = useCallback(
    (value: number) =>
      form.setValue("maxFollowers", value === rangeMax ? undefined : String(value), {
        shouldDirty: true,
        shouldTouch: true,
      }),
    [form, rangeMax],
  );

  return (
    <div className="flex flex-col gap-2">
      <LabelWithTooltip
        label="Followers"
        tooltip="Followers"
        description="Total number of followers on the creator's account."
      />
      <SimpleRangeSlider
        min={rangeMin}
        max={rangeMax}
        minValue={Math.min(minValue, maxValue)}
        maxValue={Math.max(maxValue, minValue)}
        onMinChange={handleMinChange}
        onMaxChange={handleMaxChange}
        format="followers"
        step={1000}
      />
    </div>
  );
});
